import {
    Modal, ModalDialog, ModalOverflow, ModalClose,
    Box, Stack, Typography, Button, Divider,
    DialogTitle, DialogContent, DialogActions, CircularProgress, Alert
} from "@mui/joy";
import { useContext, useEffect, useRef, useState } from "react";
import { LayoutContext } from "../../context";
import { AnimatePresence, motion } from "framer-motion";
import { APIKonsolListScenarios, APIKonsolCreateInvite, APIKonsolCheckInviteStatus } from "../../api";

import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';


// Match NPD without mistaking an entrepreneur's NPD scenario for an individual.
export function scenarioCandidates(rows, taxKey) {
    return rows.filter(row => {
        const name = String(row.name || row.title || '').toLowerCase();
        const ip = /(^|[^а-яa-z])ип([^а-яa-z]|$)|предприним|entrepreneur/.test(name);
        const npd = /самозан|нпд|npd/.test(name);
        const usn = /усн|usn/.test(name);
        return taxKey === 'self_employed' ? npd && !ip
            : taxKey === 'entrepreneur_npd' ? ip && npd : usn || (ip && !npd);
    });
}
function safeInviteURL(value) {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && ['app.konsol.ru', 'app.konsol.pro'].includes(url.hostname)
            && !url.username && !url.password ? url.href : null;
    } catch { return null; }
}
const TAX_OPTIONS = [
    {
        key: "self_employed",
        label: "Самозанятый",
        description: "Налог на профессиональный доход (НПД), ставка 4–6%",
        icon: <ReceiptLongRoundedIcon />,
        // scenario_id будет подставлен динамически из API, 
        // либо вы можете прописать его вручную ниже:
        // scenario_id: "ВАШ_SCENARIO_ID"
    },
    {
        key: "entrepreneur_usn",
        label: "ИП на УСН",
        description: "Упрощённая система налогообложения",
        icon: <AccountBalanceWalletRoundedIcon />,
        // scenario_id: "ВАШ_SCENARIO_ID"
    },
    {
        key: "entrepreneur_npd",
        label: "ИП на НПД",
        description: "ИП с применением налога на профессиональный доход",
        icon: <ReceiptLongRoundedIcon />,
        // scenario_id: "ВАШ_SCENARIO_ID"
    },
];


export default function ModalKonsolConnect() {
    const { modalKonsolConnect, setModalKonsolConnect, account, setAccount, tokenEmployee } = useContext(LayoutContext);

    // Шаги: "select" | "info" | "checking" | "success" | "failed"
    const [step, setStep] = useState("select");
    const [selectedTax, setSelectedTax] = useState(null);
    const [inviteLink, setInviteLink] = useState(null);
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [checkLoading, setCheckLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');
    const [catalogError, setCatalogError] = useState('');
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [scenarioChoice, setScenarioChoice] = useState('');
    const sequence = useRef(0), catalogSequence = useRef(0);
    const busy = loading || checkLoading;


    const hasInvite = !!account?.id_invite_konsol;
    const hasContractor = !!account?.id_contractor_konsol;

    async function loadScenarios() {
        const current = ++catalogSequence.current;
        setCatalogLoading(true); setCatalogError('');
        try {
            const data = await APIKonsolListScenarios();
            if (catalogSequence.current !== current) return;
            if (!Array.isArray(data)) throw new Error('Сервис вернул некорректный список сценариев.');
            setScenarios(data);
        } catch (failure) {
            if (catalogSequence.current === current) setCatalogError(failure.message || 'Не удалось загрузить сценарии.');
        } finally { if (catalogSequence.current === current) setCatalogLoading(false); }
    }
    useEffect(() => {
        if (!modalKonsolConnect) return undefined;
        sequence.current++;
        setStep(hasContractor ? 'success' : hasInvite ? 'info' : 'select');
        setInviteLink(safeInviteURL(account?.id_url_konsol)); setSelectedTax(null); setScenarioChoice('');
        setError(''); setCopied(false); setLoading(false); setCheckLoading(false);
        // Existing invitations also need the catalog so their scenario can change.
        if (!hasContractor) loadScenarios();
        return () => { sequence.current++; catalogSequence.current++; };
    }, [modalKonsolConnect, account?.id]);

    function close() {
        if (busy) return;
        setModalKonsolConnect(false);
        setStep("select");
        setSelectedTax(null);
        setInviteLink(null);
        setCopied(false);
    }

    function changeScenario() {
        setSelectedTax(null); setScenarioChoice(''); setError(''); setCopied(false); setStep('select');
        if (!scenarios.length && !catalogLoading) loadScenarios();
    }
    function returnToInvite() {
        setInviteLink(safeInviteURL(account?.id_url_konsol)); setError(''); setStep('info');
    }
    async function saveScenario(taxOption, scenarioId) {
        if (busy || !scenarioId) return;
        const current = ++sequence.current;
        setSelectedTax(taxOption); setLoading(true); setError('');
        try {
            const data = await APIKonsolCreateInvite(account.id, scenarioId);
            if (sequence.current !== current) return;
            if (!data || data.error || !(data.id || data.invite_id)) throw new Error('Консоль.HRM не подтвердила приглашение. Проверьте статус.');
            const link = safeInviteURL(data.onboarding_url);
            setInviteLink(link);
            setAccount(previous => ({...previous, id_invite_konsol: data.id || data.invite_id,
                id_url_konsol: link, ...(data.contractor_id ? {id_contractor_konsol: data.contractor_id} : {})}));
            setStep(data.is_contractor ? 'success' : 'info');
        } catch (failure) {
            if (sequence.current === current) setError(failure.message || 'Не удалось сохранить сценарий.');
        } finally { if (sequence.current === current) setLoading(false); }
    }
    function handleSelectTax(taxOption) {
        if (busy || catalogLoading || catalogError) return;
        const candidates = scenarioCandidates(scenarios, taxOption.key);
        setSelectedTax(taxOption); setScenarioChoice(''); setError('');
        if (candidates.length === 1) saveScenario(taxOption, candidates[0].id || candidates[0].scenario_id);
        // If names are custom/ambiguous, show an explicit provider scenario list.
    }
    async function handleCheckConnection() {
        if (busy) return;
        const current = ++sequence.current;
        setCheckLoading(true); setError('');
        try {
            const data = await APIKonsolCheckInviteStatus(account.id);
            if (sequence.current !== current) return;
            if (!data || data.error) throw new Error('Не удалось проверить подключение.');
            if (data.invite_id) {
                const link = safeInviteURL(data.onboarding_url);
                setInviteLink(link);
                setAccount(previous => ({...previous, id_invite_konsol: data.invite_id, id_url_konsol: link}));
            }
            if (data.is_contractor) {
                setAccount(previous => ({...previous, id_contractor_konsol: data.contractor_id}));
                setStep('success');
            } else setStep('failed');
        } catch (failure) {
            if (sequence.current === current) setError(failure.message || 'Не удалось проверить подключение.');
        } finally { if (sequence.current === current) setCheckLoading(false); }
    }

    async function copyLink() {
        if (!inviteLink) return;
        try { await navigator.clipboard.writeText(inviteLink); setCopied(true); }
        catch { setError('Выделите ссылку и скопируйте её вручную.'); }
    }

    return (
        <AnimatePresence>
            {modalKonsolConnect && (
                <Modal
                    sx={{ zIndex: 100015 }}
                    open={modalKonsolConnect}
                    onClose={close}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            size="lg"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: {
                                    width: '550px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("xl")]: {
                                    width: '550px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("lg")]: {
                                    width: '550px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("md")]: {
                                    width: '450px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("sm")]: {
                                    width: '100%',
                                    m: 0
                                },
                                width: '100%',
                            })}
                        >
                            <ModalClose disabled={busy} onClick={close} />

                            <DialogTitle sx={{ gap: 1 }}>
                                <AccountBalanceWalletRoundedIcon />
                                Подключение к Консоль.HRM
                            </DialogTitle>
                            <Divider />
                            {error && <Alert color="danger">{error}</Alert>}

                            {/* ── Шаг 1: выбор системы налогообложения ── */}
                            {step === "select" && (
                                <>
                                    <DialogContent>
                                        <Typography level="body-md" sx={{ mb: 2 }}>
                                            Для получения выплат вам необходимо подключиться к сервису <strong>Консоль.HRM</strong>.
                                            Выберите вашу систему налогообложения:
                                        </Typography>

                                        <Stack spacing={1.5} sx={{ p: 1 }}>
                                            {TAX_OPTIONS.map(option => (
                                                <Button
                                                    key={option.key}
                                                    variant="outlined"
                                                    color="neutral"
                                                    loading={loading && selectedTax?.key === option.key}
                                                    disabled={busy || catalogLoading || Boolean(catalogError) || !scenarios.length}
                                                    onClick={() => handleSelectTax(option)}
                                                    startDecorator={option.icon}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        py: 1.5,
                                                        px: 2,
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        gap: 0.3,
                                                        borderRadius: 'lg',
                                                        '&:hover': { borderColor: 'primary.400', bgcolor: 'primary.50' }
                                                    }}
                                                >
                                                    <Typography level="title-sm">{option.label}</Typography>
                                                    <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                                        {option.description}
                                                    </Typography>
                                                </Button>
                                            ))}
                                        </Stack>
                                        {catalogLoading && <Typography level="body-sm" sx={{mt: 1}}>Загружаем сценарии…</Typography>}
                                        {catalogError && <Alert color="warning" sx={{mt: 1}} endDecorator={<Button size="sm" variant="plain" onClick={loadScenarios}>Повторить</Button>}>{catalogError}</Alert>}
                                        {!catalogLoading && !catalogError && !scenarios.length && <Alert color="warning" sx={{mt: 1}}>Активных сценариев нет. Попросите менеджера проверить их в Консоль.HRM.</Alert>}
                                        {selectedTax && !loading && scenarioCandidates(scenarios, selectedTax.key).length !== 1 && !catalogError && !catalogLoading && <Stack spacing={1} sx={{mt: 2}}>
                                            <Typography level="body-sm">Выберите точный сценарий из Консоль.HRM:</Typography>
                                            <select aria-label="Сценарий в Консоль.HRM" value={scenarioChoice} onChange={event => setScenarioChoice(event.target.value)} disabled={busy}
                                                style={{width: '100%', minHeight: 40, borderRadius: 10, padding: '8px', background: 'transparent', color: 'inherit', border: '1px solid #cbd5e1'}}>
                                                <option value="">Выберите сценарий</option>
                                                {(scenarioCandidates(scenarios, selectedTax.key).length ? scenarioCandidates(scenarios, selectedTax.key) : scenarios).map(row => <option key={row.id || row.scenario_id} value={row.id || row.scenario_id}>{row.name || row.title || row.id}</option>)}
                                            </select>
                                            <Button disabled={!scenarioChoice || busy} onClick={() => saveScenario(selectedTax, scenarioChoice)}>{hasInvite ? 'Сохранить сценарий' : 'Получить приглашение'}</Button>
                                        </Stack>}
                                        {hasInvite && <Typography level="body-xs" sx={{mt: 2}}>До завершения регистрации и создания договора сценарий можно изменить. Готовые договоры меняются через менеджера.</Typography>}
                                    </DialogContent>
                                    <DialogActions sx={{flexWrap: 'wrap', gap: 1}}>
                                        {hasInvite && <Button variant="plain" startDecorator={<ArrowBackRoundedIcon/>} disabled={busy} onClick={returnToInvite}>К приглашению</Button>}
                                        <Button variant="plain" color="neutral" disabled={busy} onClick={close}>Отмена</Button>
                                    </DialogActions>
                                </>
                            )}

                            {/* ── Шаг 2: инфо + ссылка приглашения ── */}
                            {step === "info" && (
                                <>
                                    <DialogContent>
                                        <Stack spacing={2}>
                                            <Alert
                                                color="primary"
                                                variant="soft"
                                                startDecorator={<HourglassEmptyRoundedIcon />}
                                            >
                                                Приглашение в Консоль.HRM готово.
                                                Перейдите по ссылке ниже, зарегистрируйтесь и дождитесь проверки документов.
                                            </Alert>

                                            {inviteLink ? (
                                                <Box sx={{
                                                    p: 2,
                                                    borderRadius: 'lg',
                                                    bgcolor: 'background.level1',
                                                    border: '1px solid',
                                                    borderColor: 'neutral.200',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}>
                                                    <Typography
                                                        level="body-sm"
                                                        sx={{
                                                            wordBreak: 'break-all',
                                                            color: 'primary.500',
                                                            flex: 1,
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => window.open(inviteLink, '_blank', 'noopener,noreferrer')}
                                                    >
                                                        {inviteLink}
                                                    </Typography>
                                                    <Button
                                                        size="sm"
                                                        variant="soft"
                                                        color={copied ? "success" : "primary"}
                                                        startDecorator={<LinkRoundedIcon />}
                                                        onClick={copyLink}
                                                        sx={{ flexShrink: 0 }}
                                                    >
                                                        {copied ? "Скопировано!" : "Копировать"}
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                                    Актуальную ссылку можно получить у менеджера в Консоль.HRM или в приглашении из SMS.
                                                </Typography>
                                            )}

                                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                                После регистрации нажмите кнопку <strong>«Я подключился»</strong> — система проверит ваш статус.
                                                Если документы ещё на проверке, попробуйте позже.
                                            </Typography>
                                        </Stack>
                                    </DialogContent>
                                    <DialogActions sx={{flexWrap: 'wrap', gap: 1}}>
                                        <Button variant="soft" startDecorator={<SwapHorizRoundedIcon/>} disabled={busy} onClick={changeScenario}>Изменить сценарий</Button>
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            loading={checkLoading}
                                            onClick={handleCheckConnection}
                                            startDecorator={<CheckCircleRoundedIcon />}
                                        >
                                            Я подключился
                                        </Button>
                                        <Button variant="plain" color="neutral" onClick={close}>Отмена</Button>
                                    </DialogActions>
                                </>
                            )}

                            {/* ── Шаг 3: успех ── */}
                            {step === "success" && (
                                <>
                                    <DialogContent>
                                        <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                                            <CheckCircleRoundedIcon sx={{ fontSize: 64, color: 'success.500' }} />
                                            <Typography level="h4" textAlign="center">
                                                Вы успешно подключились!
                                            </Typography>
                                            <Typography level="body-md" textAlign="center" sx={{ color: 'text.secondary' }}>
                                                Ваш аккаунт в <strong>Консоль.HRM</strong> подтверждён.
                                                Изменение типа занятости или готового договора выполняется через менеджера в Консоль.HRM.
                                            </Typography>
                                        </Stack>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button variant="solid" color="success" onClick={close}>
                                            Отлично, закрыть
                                        </Button>
                                    </DialogActions>
                                </>
                            )}

                            {/* ── Шаг 4: не подключён / на проверке ── */}
                            {step === "failed" && (
                                <>
                                    <DialogContent>
                                        <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                                            <HourglassEmptyRoundedIcon sx={{ fontSize: 64, color: 'warning.400' }} />
                                            <Typography level="h4" textAlign="center">
                                                Подключение не подтверждено
                                            </Typography>
                                            <Typography level="body-md" textAlign="center" sx={{ color: 'text.secondary' }}>
                                                Возможно, ваши документы ещё проходят проверку. Это может занять некоторое время.
                                                Попробуйте нажать «Я подключился» позже.
                                            </Typography>
                                        </Stack>
                                    </DialogContent>
                                    <DialogActions sx={{flexWrap: 'wrap', gap: 1}}>
                                        <Button variant="soft" startDecorator={<SwapHorizRoundedIcon/>} disabled={busy} onClick={changeScenario}>Изменить сценарий</Button>
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            loading={checkLoading}
                                            onClick={handleCheckConnection}
                                        >
                                            Проверить снова
                                        </Button>
                                        <Button variant="plain" color="neutral" onClick={close}>Отмена</Button>
                                    </DialogActions>
                                </>
                            )}

                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}
